import { useAuth } from '@/hooks/useAuth';
import { hasPermission } from '@/lib/permissions';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { NAVIGATION_GROUPS, STANDALONE_NAVIGATION_ITEMS } from './constants';
import type { NavigationGroup } from './types';

export const useNavigation = () => {
  const { user } = useAuth();
  const location = useLocation();

  const filteredStandaloneItems = useMemo(() => {
    if (!user) {
      return [];
    }

    return STANDALONE_NAVIGATION_ITEMS.filter(item => {
      if (!item.requiredPermission) {
        return true;
      }

      return hasPermission(
        user,
        item.requiredPermission.entity,
        item.requiredPermission.action
      );
    });
  }, [user]);

  const filteredNavigationGroups = useMemo(() => {
    if (!user) {
      return [];
    }

    return NAVIGATION_GROUPS.map(group => ({
      ...group,
      items: group.items.filter(item => {
        if (!item.requiredPermission) {
          return true;
        }

        return hasPermission(
          user,
          item.requiredPermission.entity,
          item.requiredPermission.action
        );
      }),
    })).filter(group => group.items.length > 0);
  }, [user]);

  const isActive = useCallback(
    (path: string): boolean => location.pathname === path,
    [location.pathname]
  );

  const isGroupDefaultOpen = useCallback(
    (group: NavigationGroup): boolean =>
      group.items.some(item => isActive(item.path || '')),
    [isActive]
  );

  return {
    filteredStandaloneItems,
    filteredNavigationGroups,
    isActive,
    isGroupDefaultOpen,
  };
};
